"use client"

import { useState, useEffect, useRef } from "react"
import { getDatabase, ref as dbRef, update, set, push, get, onValue } from "firebase/database"
import { getAuth } from "firebase/auth"
import {
  FaInfoCircle, FaCheckCircle,
  FaTruck,
  FaTimes,
  FaStop,
  FaEllipsisV,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaBoxOpen,
  FaUpload,
  FaRupeeSign,
  FaArrowRight,
} from "react-icons/fa"
import { useNavigate, useLocation } from "react-router-dom"
import "./OrderStatus.css"
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"
import { useAccount } from "../context/AccountContext"
import UserTransportOrders from "../Transporter/UserTransportOrders";
import ClientCreateRequest from "../Transporter/ClientCreateRequest";
import { MdInfo } from "react-icons/md"

const gifUrl = "/Images/New notification.gif"

const getIndianTimestamp = () => {
  const now = new Date()
  const istOffset = 5.5 * 60 * 60 * 1000
  const istTime = new Date(now.getTime() + istOffset)
  return istTime.toISOString().replace("T", " ").replace("Z", "")
}
// Helper: Get the most recent meaningful timestamp for an order
const getLastUpdatedTimestamp = (order) => {
  if (order.statusUpdatedAt) return order.statusUpdatedAt
  if (order.dispatchTransactions) {
    const txns = Object.values(order.dispatchTransactions)
    if (txns.length > 0) {
      const latest = txns.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0]
      if (latest?.timestamp) return latest.timestamp
    }
  }
  if (order.paymentTimestamp) return order.paymentTimestamp
  return order.timestamp || new Date().toISOString()
}

// Relative time formatter
const formatRelativeTime = (timestamp) => {
  const now = new Date()
  const then = new Date(timestamp)
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return then.toLocaleDateString("en-IN")
}

const sendNotification = async (userId, statusId, brand, company_name, action, siteAddress) => {
  const db = getDatabase()
  const stopOrderRef = dbRef(db, `supplier_notifications/Stop_Order/${userId}`)

  let title, message
  if (action === "pause") {
    title = "Order Paused"
    message = `Order has been paused by ${company_name}. Please review the order details at ${siteAddress}.`
  } else if (action === "continue") {
    title = "Order Continued"
    message = `Order has been continued by ${company_name}. Please review the order details at ${siteAddress}.`
  } else if (action === "payment") {
    title = "Payment Added"
    message = `Payment has been added by ${company_name}. Please review the order details at ${siteAddress}.`
  } else {
    title = "Order Status Update"
    message = `Order status has been updated by ${company_name}. Please review the order details at ${siteAddress}.`
  }

  const newNotificationRef = push(stopOrderRef)
  const newNotification = {
    orderId: statusId,
    brand,
    title,
    message,
    gifUrl,
    timestamp: getIndianTimestamp(),
    seen: false,
  }

  try {
    await set(newNotificationRef, newNotification)
  } catch (error) {
    console.error("Error sending notification:", error)
  }
}

const PaymentsendNotification = async (userId, statusId, brand, company_name, action, siteAddress) => {
  const db = getDatabase()
  const stopOrderRef = dbRef(db, `supplier_notifications/PaymentProofAdded/${userId}`)

  let title, message
  if (action === "Payment Proof Added") {
    title = "Payment Proof Added"
    message = `Payment proof has been added by ${company_name}. Please review the order details at ${siteAddress}.`
  } else {
    console.warn("Unknown action for notification:", action)
    return
  }

  const newNotificationRef = push(stopOrderRef)
  const newNotification = {
    orderId: statusId,
    brand,
    title,
    message,
    gifUrl,
    timestamp: getIndianTimestamp(),
    seen: false,
  }

  try {
    await set(newNotificationRef, newNotification)
  } catch (error) {
    console.error("Error sending notification:", error)
  }
}

const OrderStatus = ({ targetFirmAndMainIds, isPartner }) => {
  const navigate = useNavigate()
  const { userId, loading: userLoading } = useGetCurrentUserId()
  const { currentAccount } = useAccount()
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [filter, setFilter] = useState("All")
  const [orderType, setOrderType] = useState("FOR") // "FOR" | "EX"
  const [activeView, setActiveView] = useState("orders");
  const [hasEXTransport, setHasEXTransport] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null)
  const [showTrackingModal, setShowTrackingModal] = useState(false)
  const [showStopSupplyModal, setShowStopSupplyModal] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [currentOrderId, setCurrentOrderId] = useState(null)
  const [hoveredOrderId, setHoveredOrderId] = useState(null)
  const [hoveredMenu, setHoveredMenu] = useState(null)
  const [stopReasons, setStopReasons] = useState({
    siteProblems: false,
    labourProblems: false,
    rainIssue: false,
    storageConstraint: false,
    other: false,
  })
  const [fileError, setFileError] = useState("");
  const [otherReason, setOtherReason] = useState("")
  const [company_name, setCompanyName] = useState("")
  const [showContinueConfirmationModal, setShowContinueConfirmationModal] = useState(false)
  // Track when user last viewed each order (from Firebase)
  const [viewedTimestamps, setViewedTimestamps] = useState({})
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentData, setPaymentData] = useState({
    pending: 0,
    rate: 0,
    balance: 0,
    paymentAmount: 0,
    paymentProof: null,
  })
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [showDelayModal, setShowDelayModal] = useState(false)
  const [selectedDelayOrder, setSelectedDelayOrder] = useState(null)
  const [showEXNotice, setShowEXNotice] = useState(false);
  const [transportTypes, setTransportTypes] = useState({});
  const orderRefs = useRef({})
  const fileInputRef = useRef(null)

  const auth = getAuth()
  const database = getDatabase()

  useEffect(() => {
    if (currentAccount?.company_name) {
      setCompanyName(currentAccount.company_name)
    }
  }, [currentAccount])

  // Load viewed timestamps from Firebase
  useEffect(() => {
    if (!userId) return
    const viewedRef = dbRef(database, `users/${userId}/viewedOrders`)
    const unsubscribe = onValue(viewedRef, (snap) => {
      setViewedTimestamps(snap.val() || {})
    })
    return () => unsubscribe()
  }, [userId])

  // Check if order was updated after user last viewed it
  const isRecentlyUpdated = (order) => {
    // Hide for Pending orders
    if (order.status === "Pending") return false

    if (!order.id) return false

    const lastViewed = viewedTimestamps[order.id]

    // If user has NEVER viewed this order → show "Updated" badge
    if (!lastViewed) return true

    const lastUpdated = getLastUpdatedTimestamp(order)
    return new Date(lastUpdated) > new Date(lastViewed)
  }
  // Mark order as viewed
  const markOrderAsViewed = async (orderId) => {
    if (!userId || !orderId) return
    try {
      await set(dbRef(database, `users/${userId}/viewedOrders/${orderId}`), new Date().toISOString())
    } catch (err) {
      console.warn("Failed to mark order as viewed", err)
    }
  }

  // Helper: is this an Ex-Works (EX) order?
  const isExWorksOrder = (order) => {
    return order.siteAddress?.transportation_type?.trim().toUpperCase() === "EX"
  }

  // Apply both status filter and FOR/EX type filter
  const applyFilters = (allOrders, statusFilter, typeFilter) => {
    let result = allOrders

    // 1. Filter by FOR / EX
    if (typeFilter === "EX") {
      result = result.filter((order) => isExWorksOrder(order))
    } else {
      // FOR: everything that is NOT EX
      result = result.filter((order) => !isExWorksOrder(order))
    }

    // 2. Filter by status
    if (statusFilter !== "All") {
      result = result.filter((order) => order.status === statusFilter)
    }

    return result
  }
  // Check if user has any EX (Ex-Works) sites
useEffect(() => {
  if (!userId) {
    setHasEXTransport(false);
    return;
  }

  const codesRef = dbRef(database, `new-code/${userId}/codes`);

  const unsubscribe = onValue(codesRef, (snapshot) => {
    if (!snapshot.exists()) {
      setHasEXTransport(false);
      return;
    }

    const codesData = snapshot.val();
    let foundEX = false;

    Object.values(codesData).forEach((codeData) => {
      const sites = codeData?.sites || {};
      Object.values(sites).forEach((site) => {
        if (site?.transportation_type?.trim().toUpperCase() === "EX") {
          foundEX = true;
        }
      });
    });

    setHasEXTransport(foundEX);
  });

  return () => unsubscribe();
}, [userId]);


  const handleFilterChange = (status) => {
    setFilter(status)
    setFilteredOrders(applyFilters(orders, status, orderType))
  }

  const handleOrderTypeChange = (type) => {
    setOrderType(type)
    setFilteredOrders(applyFilters(orders, filter, type))
  }

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (isPartner) return
    try {
      const user = auth.currentUser
      if (!user || !userId) throw new Error("User not authenticated")
      const orderRef = dbRef(database, `orders/${userId}/${orderId}`)
      await update(orderRef, {
        status: newStatus,
        statusUpdatedAt: new Date().toISOString(),
      })
    } catch (error) {
      setError("Error updating order status")
    }
  }

  const openTrackingModal = async (order) => {
    const allTransactions = order.dispatchTransactions
      ? getAllTransactions(order.dispatchTransactions)
      : []

    // Mark as viewed in Firebase
    await markOrderAsViewed(order.id)

    setTrackingOrder({ ...order, allTransactions })
    setShowTrackingModal(true)
    document.body.style.overflow = "hidden"
  }

  const closeTrackingModal = () => {
    setShowTrackingModal(false)
    setTrackingOrder(null)
    document.body.style.overflow = ""
  }

  const showStopOrderConfirmation = (orderId) => {
    setCurrentOrderId(orderId)
    setShowConfirmationModal(true)
    document.body.style.overflow = "hidden"
  }

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false)
    setCurrentOrderId(null)
    document.body.style.overflow = ""
  }

  const confirmStopOrder = () => {
    closeConfirmationModal()
    openStopSupplyModal(currentOrderId)
  }

  const openStopSupplyModal = (orderId) => {
    setCurrentOrderId(orderId)
    setShowStopSupplyModal(true)
    document.body.style.overflow = "hidden"
  }

  const closeStopSupplyModal = () => {
    setShowStopSupplyModal(false)
    setCurrentOrderId(null)
    setStopReasons({
      siteProblems: false,
      labourProblems: false,
      rainIssue: false,
      storageConstraint: false,
      other: false,
    })
    setOtherReason("")
    document.body.style.overflow = ""
  }

  const handleReasonChange = (reason) => {
    setStopReasons({
      ...stopReasons,
      [reason]: !stopReasons[reason],
    })
  }

  const handleSubmitStopSupply = async () => {
    if (isPartner) return
    try {
      const user = auth.currentUser
      if (!user || !userId) throw new Error("User not authenticated")
      const orderRef = dbRef(database, `orders/${userId}/${currentOrderId}`)
      const currentOrder = orders.find((order) => order.id === currentOrderId)
      if (!currentOrder) throw new Error("Order not found")
      const selectedReasons = Object.keys(stopReasons).filter((key) => stopReasons[key])
      const pauseData = {
        status: "Paused",
        pausedAt: new Date().toISOString(),
        // statusUpdatedAt: new Date().toISOString(),
        pauseReasons: selectedReasons,
        otherReason: stopReasons.other ? otherReason : "",
      }
      await update(orderRef, pauseData)
      const siteAddress = currentOrder.siteAddress
        ? `${currentOrder.siteAddress.address}, ${currentOrder.siteAddress.state}, ${currentOrder.siteAddress.pincode}`
        : "N/A"
      await sendNotification(
        user.uid,
        currentOrderId,
        currentOrder.brand,
        company_name || user.displayName || "Customer",
        "pause",
        siteAddress,
      )
      closeStopSupplyModal()
      alert("Order paused successfully.")
      setTimeout(() => setError(""), 3000)
    } catch (error) {
      setError("Error pausing order")
      console.error("Error pausing order:", error)
    }
  }

  const handleContinueOrder = (orderId) => {
    setCurrentOrderId(orderId)
    setShowContinueConfirmationModal(true)
    document.body.style.overflow = "hidden"
  }

  const closeContinueConfirmationModal = () => {
    setShowContinueConfirmationModal(false)
    setCurrentOrderId(null)
    document.body.style.overflow = ""
  }

  const confirmContinueOrder = async () => {
    if (isPartner) return
    try {
      const user = auth.currentUser
      if (!user || !userId) throw new Error("User not authenticated")
      const orderRef = dbRef(database, `orders/${userId}/${currentOrderId}`)
      const currentOrder = orders.find((order) => order.id === currentOrderId)
      if (!currentOrder) throw new Error("Order not found")
      await update(orderRef, {
        status: "Continued",
        continuedAt: new Date().toISOString(),
        // statusUpdatedAt: new Date().toISOString(),
      })
      const siteAddress = currentOrder.siteAddress
        ? `${currentOrder.siteAddress.address}, ${currentOrder.siteAddress.state}, ${currentOrder.siteAddress.pincode}`
        : "N/A"
      await sendNotification(
        user.uid,
        currentOrderId,
        currentOrder.brand,
        company_name || user.displayName || "Customer",
        "continue",
        siteAddress,
      )
      closeContinueConfirmationModal()
      alert("Order continued successfully.")
      setTimeout(() => setError(""), 3000)
    } catch (error) {
      setError("Error continuing order")
      console.error("Error continuing order:", error)
    }
  }

  const handleRaiseIssue = (order) => {
    if (isPartner) return
    const siteAddress = order.siteAddress
      ? `${order.siteAddress.address}, ${order.siteAddress.state}, ${order.siteAddress.pincode}`
      : "N/A"
    const dispatchDetails =
      `Status: ${order.status}, ${order.latestTransaction?.timestamp ? `Latest Dispatch: ${order.latestTransaction.timestamp}` : ""} ${order.dispatchQty ? `Total Dispatch Qty: ${order.dispatchQty}` : ""
        }`.trim()
    navigate("/dashboard/raise-issue", {
      state: {
        brand: order.brand,
        grade: order.grade,
        siteAddress: siteAddress,
        dispatchDetails: dispatchDetails,
      },
    })
  }

  const openPaymentModal = async (order) => {
    if (isPartner) return
    setSelectedOrder(order)
    setPaymentLoading(true)
    try {
      const user = auth.currentUser
      if (!user || !userId) throw new Error("User not authenticated")
      let pendingQty = 0
      if (order.dispatchTransactions) {
        const latestTransaction = getLatestTransaction(order.dispatchTransactions)
        pendingQty = latestTransaction ? latestTransaction.pending || 0 : order.quantity || 0
      } else {
        pendingQty = order.quantity || 0
      }
      const rate = order.rate || 0
      let balance = 0
      const orderCode = order.code || null
      if (orderCode) {
        const newCodeRef = dbRef(database, `new-code/${userId}/codes`)
        const newCodeSnapshot = await get(newCodeRef)
        if (newCodeSnapshot.exists()) {
          const codesData = newCodeSnapshot.val()
          for (const [codeKey, codeData] of Object.entries(codesData)) {
            if (codeData.id === orderCode || codeData.codeID === orderCode) {
              balance = Number(codeData.balance) || 0
              break
            }
          }
        }
      }
      const totalRequired = pendingQty * rate
      const extraPaymentNeeded = Math.max(0, totalRequired - balance)
      setPaymentData({
        pending: pendingQty,
        rate: rate,
        balance: balance,
        totalRequired: totalRequired,
        paymentAmount: extraPaymentNeeded,
        paymentProof: null,
      })
      setPaymentLoading(false)
      setShowPaymentModal(true)
      document.body.style.overflow = "hidden"
    } catch (error) {
      console.error("Error fetching payment data:", error)
      setPaymentLoading(false)
      setError("Error fetching payment data")
    }
  }

  const closePaymentModal = () => {
    setShowPaymentModal(false)
    setFileError("");
    setError("");
    setSelectedOrder(null)
    setPaymentData({
      pending: 0,
      rate: 0,
      balance: 0,
      totalRequired: 0,
      paymentAmount: 0,
      paymentProof: null,
    })
    setPaymentSuccess(false)
    document.body.style.overflow = ""
  }

  const handlePaymentAmountChange = (e) => {
    setPaymentData({
      ...paymentData,
      paymentAmount: Number.parseFloat(e.target.value) || 0,
    })
  }

  const handleFileChange = (e) => {
    setFileError(""); // clear previous error

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      const MAX_SIZE = 5 * 1024 * 1024; // 5MB

      // ── Size check ──
      if (file.size > MAX_SIZE) {
        setFileError("File is too large! Maximum allowed size is 5MB.");
        e.target.value = null; // clear input
        return;
      }

      // Optional: stricter mime-type check
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setFileError("Only JPG, PNG and PDF files are allowed.");
        e.target.value = null;
        return;
      }

      // All good → save file
      setPaymentData({
        ...paymentData,
        paymentProof: file,
      });
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentData.paymentProof) {
      setError("Please upload payment proof");
      setTimeout(() => setError(""), 3000);
      return;
    }

    // Extra safety: even if somehow big file got through
    if (paymentData.paymentProof.size > 5 * 1024 * 1024) {
      setError("Payment proof file is too large (max 5MB)");
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (
      (!paymentData.paymentAmount || paymentData.paymentAmount <= 0) &&
      paymentData.balance >= paymentData.totalRequired
    ) {
      if (!paymentData.paymentProof) {
        setError("No payment needed. Your balance is sufficient.")
        setTimeout(() => setError(""), 3000)
        return
      }
    }

    setPaymentLoading(true)
    try {
      const user = auth.currentUser
      if (!user || !userId) throw new Error("User not authenticated")
      const storage = getStorage()
      const paymentProofRef = storageRef(storage, `PaymentFiles/${userId}/${paymentData.paymentProof.name}`)
      await uploadBytes(paymentProofRef, paymentData.paymentProof)
      const paymentProofUrl = await getDownloadURL(paymentProofRef)
      const orderRef = dbRef(database, `orders/${userId}/${selectedOrder.id}`)
      await update(orderRef, {
        status: "Pending",
        // statusUpdatedAt: new Date().toISOString(),
        paymentFileUrl: paymentProofUrl,
        paymentAmount: paymentData.paymentAmount,
        paymentTimestamp: getIndianTimestamp(),
      })
      const orderCode = selectedOrder.code || null
      if (orderCode) {
        const newCodeRef = dbRef(database, `new-code/${userId}/codes`)
        const newCodeSnapshot = await get(newCodeRef)
        if (newCodeSnapshot.exists()) {
          const codesData = newCodeSnapshot.val()
          for (const [codeKey, codeData] of Object.entries(codesData)) {
            if (codeData.id === orderCode || codeData.codeID === orderCode) {
              const currentBalance = codeData.balance || 0
              const updatedBalance = currentBalance + paymentData.paymentAmount
              const balanceRef = dbRef(database, `new-code/${userId}/codes/${codeKey}/balance`)
              await set(balanceRef, updatedBalance)
              break
            }
          }
        }
      }
      const siteAddress = selectedOrder.siteAddress
        ? `${selectedOrder.siteAddress.address}, ${selectedOrder.siteAddress.state}, ${selectedOrder.siteAddress.pincode}`
        : "N/A"
      await PaymentsendNotification(
        user.uid,
        selectedOrder.id,
        selectedOrder.brand,
        company_name || user.displayName || "Customer",
        "Payment Proof Added",
        siteAddress,
      )
      setPaymentLoading(false)
      setPaymentSuccess(true)
      setTimeout(() => {
        closePaymentModal()
        const updatedOrders = orders.map((order) => {
          if (order.id === selectedOrder.id) {
            return {
              ...order,
              status: "Pending",
              // statusUpdatedAt: new Date().toISOString(),
              paymentFileUrl: paymentProofUrl,
              paymentAmount: paymentData.paymentAmount,
              paymentTimestamp: getIndianTimestamp(),
            }
          }
          return order
        })
        setOrders(updatedOrders)
        setFilteredOrders(applyFilters(updatedOrders, filter, orderType))
      }, 2000)
    } catch (error) {
      console.error("Error submitting payment:", error)
      setPaymentLoading(false)
      setError("Error submitting payment")
      setTimeout(() => setError(""), 3000)
    }
  }

const pendingInfo = () => {
  alert(
    "View your orders status along with brand, grade, site address, and quantity. You can track your order status in real-time. Once your order is completed, detailed tracking information such as shipping details, dispatch quantity, and dispatch date will be available under 'Track Order'. For EX orders, first click 'Get Transportation' to arrange transportation. To view and proceed with EX order dispatch, click 'Go to Dispatch'."
  );
};

  const paymentInfo = () => {
    alert(
      "This page shows your order details and calculates the total payment required. If your current balance covers the amount, no extra payment is needed. Otherwise, upload a payment proof (image or PDF, max 5MB) for the remaining amount. Click Submit Payment to confirm and update the transaction.",
    )
  }

  const renderField = (fieldValue) => {
    if (fieldValue) {
      return fieldValue
    } else {
      return "Updated soon..."
    }
  }

  const getLatestTransaction = (dispatchTransactions) => {
    if (!dispatchTransactions) return null
    const transactions = Object.entries(dispatchTransactions).map(([id, data]) => ({
      id,
      ...data,
      timestamp: data.timestamp || "",
    }))
    return (
      transactions.sort((a, b) => {
        const dateA = new Date(a.timestamp || 0)
        const dateB = new Date(b.timestamp || 0)
        return dateB - dateA
      })[0] || null
    )
  }

  const getAllTransactions = (dispatchTransactions) => {
    if (!dispatchTransactions) return []
    const transactions = Object.entries(dispatchTransactions).map(([id, data]) => ({
      id,
      ...data,
      timestamp: data.timestamp || "",
    }))
    return transactions.sort((a, b) => {
      const dateA = new Date(a.timestamp || 0)
      const dateB = new Date(b.timestamp || 0)
      return dateB - dateA
    })
  }

  // NEW: Support multiple target UIDs (main + firms)
  useEffect(() => {
    if (userLoading) {
      setLoading(true)
      return
    }

    // Decide which UIDs to listen to
    const listenToIds = Array.isArray(targetFirmAndMainIds) && targetFirmAndMainIds.length > 0
      ? targetFirmAndMainIds
      : (userId ? [userId] : [])   // fallback to old behavior

    if (listenToIds.length === 0) {
      setOrders([])
      setFilteredOrders([])
      setLoading(false)
      return
    }

    const allOrders = []
    const unsubscribers = []

    listenToIds.forEach(uid => {
      const ordersRef = dbRef(database, `orders/${uid}`)
      const unsub = onValue(ordersRef, (snapshot) => {

        // 1️⃣ Remove old orders from this UID
        for (let i = allOrders.length - 1; i >= 0; i--) {
          if (allOrders[i].sourceUid === uid) {
            allOrders.splice(i, 1)
          }
        }

        // 2️⃣ Add updated orders
        const data = snapshot.val()
        if (data) {
          Object.keys(data).forEach(key => {
            allOrders.push({
              id: key,
              sourceUid: uid,
              ...data[key]
            })
          })
        }

        // 3️⃣ Process+sort normally
        const processed = allOrders.map(order => {
          const latestTxn = order.dispatchTransactions
            ? getLatestTransaction(order.dispatchTransactions)
            : null

          const totalQty = typeof order.quantity === "string"
            ? parseInt(order.quantity, 10)
            : order.quantity || 0

          return {
            ...order,
            quantity: totalQty,
            latestTransaction: latestTxn,
          }
        })

        const filtered = processed.filter(order => {
          if (order.completed === true) {
            const refDate = order.latestTransaction?.timestamp || order.timestamp || new Date()
            const daysDiff = Math.ceil((new Date() - new Date(refDate)) / (1000 * 60 * 60 * 24))
            return daysDiff <= 30
          }
          return true
        })

        filtered.sort((a, b) => {
          const aUpdated = isRecentlyUpdated(a)
          const bUpdated = isRecentlyUpdated(b)
          if (aUpdated && !bUpdated) return -1
          if (!aUpdated && bUpdated) return 1

          return new Date(getLastUpdatedTimestamp(b)).getTime() -
            new Date(getLastUpdatedTimestamp(a)).getTime()
        })

        setOrders(filtered)
        // Apply current status + FOR/EX filters
        setFilteredOrders(applyFilters(filtered, filter, orderType))
        setLoading(false)
      })

      unsubscribers.push(unsub)
    })

    return () => unsubscribers.forEach(u => u())
  }, [targetFirmAndMainIds, userId, userLoading, viewedTimestamps])

  // Re-apply filters when orderType or filter changes (orders already loaded)
  useEffect(() => {
    if (orders.length > 0 || !loading) {
      setFilteredOrders(applyFilters(orders, filter, orderType))
    }
  }, [orderType, filter])

  useEffect(() => {
    if (!showDelayModal) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.delay-popover') &&
        !event.target.closest('.status-delay')) {
        setShowDelayModal(false);
        setSelectedDelayOrder(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDelayModal]);

  const getStatusBadge = (order) => {   // ← pass whole order, not just status
    const status = order.status

    if (status === "Delayed") {
      // ← Define it here (this is the missing line)
      const isThisOrderOpen = showDelayModal && selectedDelayOrder?.id === order.id;

      return (
        <div className="relative inline-block ">
          <span
            className={`status-delay  cursor-pointer flex items-center gap-1 ${isThisOrderOpen ? 'open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isThisOrderOpen) {
                setShowDelayModal(false);
                setSelectedDelayOrder(null);
              } else {
                setSelectedDelayOrder(order);
                setShowDelayModal(true);
              }
            }}
          >
            Delayed

          </span>

          {isThisOrderOpen && (
            <div className="delay-popover">
              <div className="delay-popover-header">
                <span>Delay Reason</span>
                <button
                  className="delay-popover-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDelayModal(false);
                    setSelectedDelayOrder(null);
                  }}
                >
                  <FaTimes size={14} />
                </button>
              </div>

              <div className="delay-item">

                <span className="delay-value">
                  {selectedDelayOrder.delay || "Not Available"}
                </span>
              </div>


            </div>
          )}
        </div>
      );
    }
    switch (status) {
      case "Pending":
        return <span className="status-badge status-pending">Pending</span>
      case "Paused":
        return <span className="status-badge status-paused">Paused</span>
      case "Continued":
        return <span className="status-badge status-continued">Continued</span>
      case "Underloading":
        return <span className="status-badge status-underloading">Underloading</span>
      case "Dispatch":
        return <span className="status-badge status-dispatch">Dispatch</span>
      case "Completed":
        return (
          <span className="status-badge status-completed">
            <FaCheckCircle /> Completed
          </span>
        )
      default:
        return <span className="status-badge">{status}</span>
    }
  }

  const renderQuantityBar = (order) => {
    if (!order.quantity) return null
    const totalQuantity = typeof order.quantity === "string" ? Number.parseInt(order.quantity, 10) : order.quantity
    const dispatchTransactions = order.dispatchTransactions || {}
    const totalDispatch = Object.values(dispatchTransactions).reduce((sum, txn) => {
      return sum + (txn.dispatch || 0)
    }, 0)
    const pendingValue = Math.max(0, totalQuantity - totalDispatch)
    const quantityType = order.quantityType || "bags"
    let borderColorClass = "quantity-card-pending"
    if (order.status === "Dispatch" || order.status === "Completed") {
      borderColorClass = "quantity-card-dispatch"
    } else if (order.status === "Paused") {
      borderColorClass = "quantity-card-paused"
    }
    let dispatchPercent = 0
    let pendingPercent = 0
    if (totalQuantity > 0) {
      dispatchPercent = (totalDispatch / totalQuantity) * 100
      pendingPercent = (pendingValue / totalQuantity) * 100
    }
    if (pendingValue === 0) {
      dispatchPercent = 100
      pendingPercent = 0
    }


    return (
      <div className={`quantity-card ${borderColorClass}`}>
        <div className="quantity-card-header">
          <span className="quantity-label">Quantity:</span>
          <span className="quantity-unit">{quantityType}</span>
        </div>
        <div className="quantity-bar-container">
          <div className="quantity-bar">
            <div className="quantity-bar-section dispatch" style={{ width: `${dispatchPercent}%` }}></div>
            {pendingPercent > 0 && (
              <div className="quantity-bar-section pending" style={{ width: `${pendingPercent}%` }}></div>
            )}
          </div>
          <div className="quantity-bar-values">
            <div className="quantity-value">
              <span className="value-number">{totalQuantity}</span>
              <span className="value-label">Total</span>
            </div>
            <div className="quantity-value">
              <span className="value-number">{pendingValue}</span>
              <span className="value-label">Pending</span>
            </div>
            <div className="quantity-value">
              <span className="value-number">{totalDispatch}</span>
              <span className="value-label">Dispatch</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderTransactionCard = (transaction, index, totalCount) => {
    if (!transaction) return null
    const dispatchNumber = totalCount - index
    return (
      <div className="transaction-card" key={transaction.id || index}>
        <div className="transaction-header">
          <div className="transaction-title">
            <FaBoxOpen className="transaction-icon" />
            <span>Dispatch {dispatchNumber}</span>
          </div>
          <div className="transaction-date">
            <FaCalendarAlt className="date-icon" />
            {transaction.timestamp ? new Date(transaction.timestamp).toLocaleString() : "No date"}
          </div>
        </div>
        <div className="transaction-details">
          <div className="transaction-detail">
            <span className="detail-label">Dispatch:</span>
            <span className="detail-value">{transaction.dispatch || 0}</span>
          </div>
          <div className="transaction-detail">
            <span className="detail-label">Pending:</span>
            <span className="detail-value">{transaction.pending || 0}</span>
          </div>
          <div className="transaction-detail">
            <span className="detail-label">Shipping Details:</span>
            <span className="detail-value">{transaction.shippingDetail || "Not available"}</span>
          </div>
        </div>
      </div>
    )
  }

  if (loading || userLoading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
      </div>
    )
  }

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";

    const date = new Date(timestamp);

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    
      <div className="pending-top-container">
      <div className="pending-header">
        <h2 className="pending-title">
          Order Status
          <MdInfo
            onClick={pendingInfo}
            style={{ cursor: "pointer", fontSize: "1.3rem", color: "grey", marginTop: "7px" }}
          />
        </h2>
      </div>
 {/* ALWAYS VISIBLE TOGGLER */}
    {/* Show FOR / EX toggle ONLY when user has at least one EX site */}
{hasEXTransport && (
  <div className="order-type-toggle-wrapper">
    <div className="order-type-toggle">
      <button
        type="button"
        className={`order-type-btn ${orderType === "FOR" ? "active" : ""}`}
        onClick={() => {
          setActiveView("orders");
          handleOrderTypeChange("FOR");
        }}
      >
        FOR Orders
      </button>

      <button
        type="button"
        className={`order-type-btn ${orderType === "EX" ? "active" : ""}`}
        onClick={() => {
          // EX toggle: open UserTransportOrders below toggler
          handleOrderTypeChange("EX");
          setActiveView("transport");
        }}
      >
        EX Orders
      </button>
    </div>

    <p className="order-type-hint">
      {orderType === "FOR" ? (
        "FOR (Freight on Road) orders"
      ) : activeView === "transport" ? (
        "EX (Ex-Works) — arrange transport & dispatch below"
      ) : (
        "EX (Ex-Works) orders"
      )}
    </p>
  </div>
)}

    {/* ================================= */}
    {/* CONTENT BELOW TOGGLER */}
    {/* ================================= */}

    {activeView === "transport" ? (
      <>
        {/* Centered section: See Pending Orders (EX Order Status) */}
        <div className="ex-action-section">
          <div className="ex-action-card">
            <div className="ex-action-text">
              <h4>EX Order</h4>
              <p>View your Ex-Works (EX) orders.</p>
            </div>
            <button
              type="button"
              className="ex-action-btn"
              onClick={() => {
                handleOrderTypeChange("EX");
                setActiveView("orders");
              }}
            >
              See Pending Orders <FaArrowRight />
            </button>
          </div>
        </div>

        <UserTransportOrders />
      </>

    ) : activeView === "createRequest" ? (

      <ClientCreateRequest
        onBack={() => setActiveView("transport")}
      />

    ) : (

      /* NORMAL ORDER STATUS CONTENT */
      <>
      {/* Centered section: Go to Dispatch / Transport (when viewing EX orders) */}
      {orderType === "EX" && (
        <div className="ex-action-section">
          <div className="ex-action-card">
            <div className="ex-action-text">
              <h4>Transport & Dispatch</h4>
              <p>Dispatches of your Ex-Works (EX) orders.</p>
            </div>
            <button
              type="button"
              className="ex-action-btn"
              onClick={() => setActiveView("transport")}
            >
              <FaTruck /> Go to Dispatch <FaArrowRight />
            </button>
          </div>
        </div>
      )}
      <div className="filter-section">
        <label htmlFor="orderFilter" className="filter-label">
          Sort By:
        </label>
        <select
          id="orderFilter"
          className="filter-select"
          value={filter}
          onChange={(e) => handleFilterChange(e.target.value)}
        >
          <option value="All">All Orders</option>
          <option value="Pending">Pending</option>
          <option value="Delayed">Delay</option>
          <option value="Paused">Paused</option>
          <option value="Continued">Continued</option>
          <option value="Underloading">Underloading</option>
          <option value="Dispatch">Dispatch</option>
          <option value="Completed">Completed</option>
        </select>
        <p className="reviewText">To review your previous orders, you can download the statements from the sidebar.</p>
      </div>
      
      <div className="pending-orders-container">
        <div className="orders-grid">
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order) => (
              <div
                key={order.id}
                ref={(el) => (orderRefs.current[order.id] = el)}
                className={`order-card status-${order.status.toLowerCase()} ${order.status === "Paused" ? "paused-order" : ""
                  }  
                }`}
                onMouseEnter={() => {
                  if (!isPartner) setHoveredOrderId(order.id)
                }}
                onMouseLeave={() => {
                  if (!isPartner) {
                    setHoveredOrderId(null)
                    setHoveredMenu(null)
                  }
                }}
              >
                <div className="order-card-header-info">

                  {/* Only show "Updated X min ago" if NOT Pending */}
                  {order.status !== "Pending" && (
                    <small className="last-updated-text">
                      Updated {formatRelativeTime(getLastUpdatedTimestamp(order))}
                    </small>
                  )}

                  {/* Updated Badge */}
                  {isRecentlyUpdated(order) && (
                    <div className="notification-badge updated">Updated</div>
                  )}
                </div>
                {/* EX alert — only relevant when viewing EX tab (orders are already filtered) */}
                {isExWorksOrder(order) && (
                  <div className="ex-order-alert">
                    <div className="ex-order-content">
                      <span className="ex-order-title">Ex-Works (EX) Order</span>
                      <p className="ex-order-text">
                        This is an ex basis order. Arrange transport or go to dispatch.
                       <span
  className="ex-order-link ml-2"
onClick={() => setActiveView("createRequest")}
>
  Get Transporter <FaArrowRight />
</span>
                      </p>
                    </div>
                  </div>
                )}
                <div className="order-header">
                  <span className="order-number">Order</span>
                  <span className="order-date">  {formatDateTime(order.timestamp)}</span>


                  <div className="menu-container">
                    {!isPartner && (
                      <button
                        className="menu-button"
                        onClick={() => setHoveredMenu((prev) => (prev === order.id ? null : order.id))}
                      >
                        <FaEllipsisV />
                      </button>
                    )}

                    {!isPartner && hoveredMenu === order.id && (
                      <div className="menu-dropdown">
                        {order.status === "Pending" && (
                          <button
                            className="menu-item stop-order-item"
                            onClick={() => showStopOrderConfirmation(order.id)}
                          >
                            <FaStop /> Pause Order
                          </button>
                        )}
                        <button className="menu-item raise-issue-item" onClick={() => handleRaiseIssue(order)}>
                          <FaExclamationTriangle /> Raise Issue
                        </button>
                        {(order.status === "Pending" ||
                          order.status === "Delayed" ||
                          order.status === "Underloading" ||
                          order.status === "Dispatch" ||
                          order.status === "Paused" ||
                          order.status === "Continued") && (
                            <button className="menu-item payment-item" onClick={() => openPaymentModal(order)}>
                              <FaRupeeSign /> Add Payment
                            </button>
                          )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="order-id-display">
                  <span className="order-id-label">CODE ID:</span>
                  <span className="order-id-value">{order.code}</span>
                </div>
                <div className="order-content">
                  <div className="order-detail">
                    <span className="detail-label">Brand:</span>
                    <span className="detail-value">{order.brand}</span>
                  </div>
                  <div className="order-detail">
                    <span className="detail-label">Grade:</span>
                    <span className="detail-value">{order.grade || "N/A"}</span>
                  </div>
                  {renderQuantityBar(order)}
                  <div className="order-detail address-detail">
                    <span className="detail-label">Site Address:</span>
                    <span className="detail-value">
                      {order.siteAddress
                        ? `${order.siteAddress.address}, ${order.siteAddress.state}, ${order.siteAddress.pincode}`
                        : "N/A"}
                    </span>
                  </div>
                </div>

                <div className="order-footer">
                  <div className="status-container">{getStatusBadge(order)}</div>
                  <button className="track-button" onClick={() => openTrackingModal(order)}>
                    <FaTruck /> Track Order
                  </button>

                </div>
                {order.status === "Dispatch" && (
                  <div className="dispatch-note">
                    <span className="note-asterisk">*</span>
                    <span className="note-text">
                      Note: Timely unloading of vehicle will help us serve you better.
                    </span>
                  </div>
                )}
                {order.status === "Paused" && hoveredOrderId === order.id && !isPartner && (
                  <div className="continue-overlay">
                    <button className="continue-button" onClick={() => handleContinueOrder(order.id)}>
                      Continue Order
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="no-orders">
              {orderType === "EX"
                ? "No EX (Ex-Works) Orders Found"
                : "No FOR Orders Found"}
            </div>
          )}
        </div>



        {showTrackingModal && trackingOrder && (
          <div className="tracking-modal-overlay">
            <div className="tracking-modal">
              <div className="tracking-modal-header">
                <h3>Order Tracking Details</h3>
                <button className="close-modal-btn" onClick={closeTrackingModal}>
                  <FaTimes />
                </button>
              </div>
              <div className="tracking-modal-content">
                <div className="tracking-order-summary">
                  <h4>Order Summary</h4>
                  <div className="summary-detail order-id-summary">
                    <span>Order ID:</span>
                    <span>{trackingOrder.id}</span>
                  </div>
                  <div className="summary-detail">
                    <span>Brand:</span>
                    <span>{trackingOrder.brand}</span>
                  </div>
                  <div className="summary-detail">
                    <span>Grade:</span>
                    <span>{trackingOrder.grade || "N/A"}</span>
                  </div>
                  <div className="summary-detail">
                    <span>Quantity:</span>
                    <span>
                      {trackingOrder.quantity || 0}{" "}
                      {trackingOrder.quantityType ? ` (${trackingOrder.quantityType})` : ""}
                    </span>
                  </div>
                  <div className="summary-detail">
                    <span>Rate:</span>
                    <span>{trackingOrder.rate || "N/A"}</span>
                  </div>
                  <div className="summary-detail">
                    <span>Payment Amount:</span>
                    <span>{trackingOrder.paymentAmount ? `₹${trackingOrder.paymentAmount}` : "Not available"}</span>
                  </div>
                  <div className="summary-detail">
                    <span>Status:</span>
                    <span>{getStatusBadge(trackingOrder)}</span>
                  </div>
                </div>
                <div className="tracking-info">
                  <h4>Tracking Information</h4>
                  <div className="tracking-detail">
                    <span className="tracking-label">Order Date:</span>
                    <span className="tracking-value">{renderField(trackingOrder.timestamp)}</span>
                  </div>
                  {trackingOrder.latestTransaction && (
                    <>
                      <div className="tracking-detail">
                        <span className="tracking-label">Shipping Detail:</span>
                        <span className="tracking-value">
                          {renderField(trackingOrder.latestTransaction.shippingDetail)}
                        </span>
                      </div>
                      <div className="tracking-detail">
                        <span className="tracking-label">Dispatch Quantity:</span>
                        <span className="tracking-value">
                          {trackingOrder.latestTransaction.dispatch
                            ? trackingOrder.latestTransaction.dispatch
                            : "Updated soon..."}
                        </span>
                      </div>
                      <div className="tracking-detail">
                        <span className="tracking-label">Pending Quantity:</span>
                        <span className="tracking-value">
                          {trackingOrder.status === "Completed"
                            ? "0"
                            : trackingOrder.latestTransaction?.pending !== undefined
                              ? trackingOrder.latestTransaction.pending
                              : trackingOrder.quantity && trackingOrder.latestTransaction?.dispatch
                                ? Math.max(0, trackingOrder.quantity - trackingOrder.latestTransaction.dispatch)
                                : "Updated soon..."}
                        </span>
                      </div>
                      <div className="tracking-detail">
                        <span className="tracking-label">Timestamp:</span>
                        <span className="tracking-value">{renderField(trackingOrder.latestTransaction.timestamp)}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="tracking-timeline">
                  <h4>Order Timeline</h4>
                  <div className="timeline">
                    <div
                      className={`timeline-item ${trackingOrder.status === "Pending" || trackingOrder.status === "Underloading" || trackingOrder.status === "Dispatch" || trackingOrder.status === "Completed" ? "active" : ""}`}
                    >
                      <div className="timeline-icon"></div>
                      <div className="timeline-content">
                        <h5>Order Placed</h5>
                        <p>{trackingOrder.timestamp || "Processing"}</p>
                      </div>
                    </div>
                    <div
                      className={`timeline-item ${trackingOrder.status === "Underloading" || trackingOrder.status === "Dispatch" || trackingOrder.status === "Completed" ? "active" : ""}`}
                    >
                      <div className="timeline-icon"></div>
                      <div className="timeline-content">
                        <h5>Underloading</h5>
                        <p>
                          {trackingOrder.status === "Underloading" ||
                            trackingOrder.status === "Dispatch" ||
                            trackingOrder.status === "Completed"
                            ? "In Progress"
                            : "Pending"}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`timeline-item ${trackingOrder.status === "Dispatch" || trackingOrder.status === "Completed" ? "active" : ""}`}
                    >
                      <div className="timeline-icon"></div>
                      <div className="timeline-content">
                        <h5>Dispatched</h5>
                        <p>{trackingOrder.latestTransaction?.timestamp || "Pending"}</p>
                      </div>
                    </div>
                    <div className={`timeline-item ${trackingOrder.status === "Completed" ? "active" : ""}`}>
                      <div className="timeline-icon"></div>
                      <div className="timeline-content">
                        <h5>Delivered</h5>
                        <p>{trackingOrder.status === "Completed" ? "Completed" : "Pending"}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="transaction-history">
                  <h4>Dispatch History</h4>
                  <div className="transactions-container">
                    {trackingOrder.allTransactions && trackingOrder.allTransactions.length > 0 ? (
                      trackingOrder.allTransactions.map((transaction, index) =>
                        renderTransactionCard(transaction, index, trackingOrder.allTransactions.length),
                      )
                    ) : (
                      <div className="no-transactions">No dispatch history available</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="tracking-modal-footer">
                <button className="close-tracking-btn" onClick={closeTrackingModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmationModal && (
          <div className="tracking-modal-overlay">
            <div className="tracking-modal confirmation-modal">
              <div className="tracking-modal-header">
                <h4>Confirm Pause Order</h4>
                <button className="close-modal-btn" onClick={closeConfirmationModal}>
                  <FaTimes />
                </button>
              </div>
              <div className="tracking-modal-content">
                <div className="confirmation-message">
                  <p>Are you sure you want to pause this order?</p>
                  <p>This action will temporarily hold the supply for this order.</p>
                </div>
              </div>
              <div className="tracking-modal-footer">
                <button className="status-cancel-btn" onClick={closeConfirmationModal}>
                  Cancel
                </button>
                <button className="confirm-btn" onClick={confirmStopOrder}>
                  Pause Order
                </button>
              </div>
            </div>
          </div>
        )}

        {showStopSupplyModal && (
          <div className="tracking-modal-overlay">
            <div className="tracking-modal stop-supply-modal">
              <div className="tracking-modal-header">
                <h3>Pause Order Request</h3>
                <button className="close-modal-btn" onClick={closeStopSupplyModal}>
                  <FaTimes />
                </button>
              </div>
              <div className="tracking-modal-content">
                <div className="stop-supply-info">
                  <h4>Select Reason(s) for Pausing Order</h4>
                  <p className="stop-supply-description">
                    Please select all applicable reasons for pausing this order.
                  </p>
                  <div className="stop-reasons-container">
                    <div className="stop-reason-item">
                      <input
                        type="checkbox"
                        id="siteProblems"
                        checked={stopReasons.siteProblems}
                        onChange={() => handleReasonChange("siteProblems")}
                      />
                      <label htmlFor="siteProblems">Site Problem</label>
                    </div>
                    <div className="stop-reason-item">
                      <input
                        type="checkbox"
                        id="labourProblems"
                        checked={stopReasons.labourProblems}
                        onChange={() => handleReasonChange("labourProblems")}
                      />
                      <label htmlFor="labourProblems">Labour Problem</label>
                    </div>
                    <div className="stop-reason-item">
                      <input
                        type="checkbox"
                        id="rainIssue"
                        checked={stopReasons.rainIssue}
                        onChange={() => handleReasonChange("rainIssue")}
                      />
                      <label htmlFor="rainIssue">Rain Issue</label>
                    </div>
                    <div className="stop-reason-item">
                      <input
                        type="checkbox"
                        id="storageConstraint"
                        checked={stopReasons.storageConstraint}
                        onChange={() => handleReasonChange("storageConstraint")}
                      />
                      <label htmlFor="storageConstraint">Storage Constraint</label>
                    </div>
                    <div className="stop-reason-item">
                      <input
                        type="checkbox"
                        id="other"
                        checked={stopReasons.other}
                        onChange={() => handleReasonChange("other")}
                      />
                      <label htmlFor="other">Other</label>
                    </div>
                  </div>
                  {stopReasons.other && (
                    <div className="other-reason-container">
                      <label htmlFor="otherReasonText">Please specify:</label>
                      <textarea
                        id="otherReasonText"
                        value={otherReason}
                        onChange={(e) => {
                          const words = e.target.value.trim().split(/\s+/)
                          if (words.length <= 100) {
                            setOtherReason(e.target.value)
                          }
                        }}
                        placeholder="Please provide details..."
                        rows={4}
                      />
                      <p>{otherReason.trim().split(/\s+/).length}/100 words</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="tracking-modal-footer">
                <button className="status-cancel-btn" onClick={closeStopSupplyModal}>
                  Cancel
                </button>
                <button
                  className="submit-stop-btn"
                  onClick={handleSubmitStopSupply}
                  disabled={
                    !Object.values(stopReasons).some((value) => value) ||
                    (stopReasons.other && otherReason.trim() === "")
                  }
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        )}

        {showPaymentModal && selectedOrder && (
          <div className="tracking-modal-overlay">
            <div className="tracking-modal payment-modal">
              <div className="tracking-modal-header">
                <h3>
                  Add Payment <FaInfoCircle onClick={paymentInfo} className="icon-i fs-5 cursor-pointer ml-2 mb-1"   style={{ cursor: "pointer" }} />
                </h3>
                <button className="close-modal-btn" onClick={closePaymentModal}>
                  <FaTimes />
                </button>
              </div>
              <div className="tracking-modal-content">
                {paymentLoading ? (
                  <div className="payment-loading">Uploading...</div>
                ) : paymentSuccess ? (
                  <div className="payment-success">
                    <FaCheckCircle className="success-icon" />
                    <p>Payment added successfully!</p>
                  </div>
                ) : (
                  <div className="payment-form">
                    <div className="payment-order-info">
                      <h4>Order Information</h4>
                      <div className="payment-order-detail">
                        <span>Order ID:</span>
                        <span>{selectedOrder.id}</span>
                      </div>
                      <div className="payment-order-detail">
                        <span>Brand:</span>
                        <span>{selectedOrder.brand}</span>
                      </div>
                      <div className="payment-order-detail">
                        <span>Site Address:</span>
                        <span>
                          {selectedOrder.siteAddress
                            ? `${selectedOrder.siteAddress.address}, ${selectedOrder.siteAddress.state}, ${selectedOrder.siteAddress.pincode}`
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className="payment-details">
                      <h4>Payment Details</h4>
                      <div className="payment-field">
                        <span className="payment-label">Pending Quantity:</span>
                        <span className="payment-value">{paymentData.pending}</span>
                      </div>
                      <div className="payment-field">
                        <span className="payment-label">Rate:</span>
                        <span className="payment-value">₹{paymentData.rate}</span>
                      </div>
                      <div className="payment-field">
                        <span className="payment-label">Total Required:</span>
                        <span className="payment-value">₹{paymentData.totalRequired.toFixed(2)}</span>
                      </div>
                      <div className="payment-row">
                        <div className="payment-field half-width">
                          <label className="payment-label">Current Balance:</label>
                          <span className="payment-value">₹{Number(paymentData.balance || 0).toFixed(2)}</span>
                        </div>
                        <div className="payment-field half-width">
                          <label className="payment-label">Payment Amount Required:</label>
                          {Number(paymentData.balance || 0) >= paymentData.totalRequired ? (
                            <span className="payment-value note-text">
                              No extra amount needed. Will be deducted from balance.
                            </span>
                          ) : (
                            <input
                              type="number"
                              id="paymentAmount"
                              className="payment-input"
                              value={paymentData.paymentAmount.toFixed(2)}
                              disabled
                            />
                          )}
                        </div>
                      </div>
                      <div className="payment-field">
                        <label htmlFor="paymentProof" className="payment-label">
                          Payment Proof:
                        </label>

                        <div className="file-input-wrapper">
                          <input
                            type="file"
                            id="paymentProof"
                            ref={fileInputRef}
                            className="file-input"
                            accept="image/*,.pdf"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />

                          <label
                            htmlFor="paymentProof"
                            className={`file-upload-label ${paymentData.paymentProof ? "uploaded" : ""}`}
                          >
                            {paymentData.paymentProof
                              ? "Payment Proof Uploaded ✓"
                              : "Upload Payment Proof"}
                          </label>
                          {fileError && (
                            <div className="file-error">
                              {fileError}
                            </div>
                          )}
                          {paymentData.paymentProof && (
                            <div className="uploaded-file-info">
                              <div className="file-name-row">
                                <span className="file-name">{paymentData.paymentProof.name} ,({(paymentData.paymentProof.size / (1024 * 1024)).toFixed(2)} MB)</span>
                              </div>
                            </div>
                          )}

                          <p className="file-hint">Max 5MB • JPG, PNG, PDF</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="tracking-modal-footer">
                <button className="status-cancel-btn" onClick={closePaymentModal}>
                  Cancel
                </button>
                {!paymentSuccess && (
                  <button className="submit-payment-btn" onClick={handleSubmitPayment}>
                    {paymentLoading ? "Processing..." : "Submit Payment"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {showContinueConfirmationModal && (
          <div className="tracking-modal-overlay">
            <div className="tracking-modal confirmation-modal">
              <div className="tracking-modal-header">
                <h4>Confirm Continue Order</h4>
                <button className="close-modal-btn" onClick={closeContinueConfirmationModal}>
                  <FaTimes />
                </button>
              </div>
              <div className="tracking-modal-content">
                <div className="confirmation-message">
                  <p>Are you sure you want to continue this order?</p>
                  <p>This action will resume the supply for this order.</p>
                </div>
              </div>
              <div className="tracking-modal-footer">
                <button className="status-cancel-btn" onClick={closeContinueConfirmationModal}>
                  Cancel
                </button>
                <button className="confirm-btn continue-confirm-btn" onClick={confirmContinueOrder}>
                  Continue Order
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      </>

    )}

  </div>

);}

export default OrderStatus;